/* eslint-disable no-magic-numbers */
/* eslint-disable complexity */
import React, { FC, useContext, useRef } from 'react';
import clsx from 'clsx';
import { WindowScroller, WindowScrollerChildProps } from 'react-virtualized';

import { FormattedMessage, useIntl } from '@futrli/localisation';

import { adjustForPastDates } from './utils/forecast-columns';
import { StickyHeaders } from './components/sticky-headers';
import { RowsType, TotalRow } from '../reports/data-grid/types';
import { ViewCollapseExpand } from '../../../ui/view-menu/view-collapse-expand';
import { DataGrid } from '../../../ui/data-grid/components/data-grid';
import { DataGridLayoutProvider } from '../../../ui/data-grid/components/data-grid-layout-context';
import { HeaderRowsVirtualized } from '../reports/data-grid/header-rows-virtualized';
import { RowsVirtualized } from '../reports/data-grid/rows-virtualized';
import { HeaderRows } from '../reports/data-grid/header-rows';
import { Rows } from '../reports/data-grid/rows';
import { SIZES } from '../../../core/constants';
import { VIEW_TYPES } from '../../../core/types';

import styles from './predictions.scss';
import useDraggableResize from '../../../hooks/useDraggableResize';
import { ForecastingTitleHeader } from './forecasting-title-header';
import { ForecastingNavigationTabs } from './navigation/forecasting-navigation-tabs';
import { DIRECTION, PeriodTableControl } from '../components/period-menu/period-table-control';
import { OverlayExplanationsContainer } from './overlay-explanations-container';
import { UserGridSettingsContext } from '../reports/user-grid-settings/user-grid-settings-context';
import { usePredictionsInfoContext } from '../context/predictions-info-context';
import { TABLE_REF_INDICES, useTotalPagesDataGrid } from './hooks';

interface Props {
  totalAccountRows?: RowsType | null;
  columns?: string[];
  totalCashPredictRow: TotalRow | null | undefined;
  isCashFlow?: boolean;
  isBalanceSheet?: boolean;
  showDaysToPay?: boolean;
}

export const PredictionsTotalsPageWrapper: FC<Props> = ({
  totalAccountRows,
  columns,
  totalCashPredictRow,
  isCashFlow,
  isBalanceSheet,
  showDaysToPay
}: Props) => {
  const intl = useIntl();

  const listContainerRef = useRef<HTMLDivElement>(null);
  const {
    isFetchingReport,
    periodType,
    totalPastDates,
    fetchForecastReport,
    viewType,
    forecast,
    rowHeaderWidth,
    setRowHeaderWidth,
    showPredictionsExplanation
  } = usePredictionsInfoContext();
  const { rowHeights } = useContext(UserGridSettingsContext);

  const { onMouseDown: onScrollContainerMouseDown } = useDraggableResize(listContainerRef, {
    direction: 'horizontal',
    onUpdate: setRowHeaderWidth,
    minX: 200
  });

  const { updateScrollLeft, handlePeriodTableControl, tables, headlineHeaderRef } = useTotalPagesDataGrid();

  const isPayments = (!isBalanceSheet && viewType === VIEW_TYPES.CASH_FLOW) || !!isCashFlow;

  if (!fetchForecastReport || !periodType) {
    return null;
  }

  const hideBudgetData = forecast?.kind === 'budget';

  const hideCashGraph = isBalanceSheet || (viewType !== VIEW_TYPES.CASH_FLOW && !isCashFlow);

  return (
    <DataGridLayoutProvider
      columnCount={columns?.length || 0}
      onScrollPositionChange={updateScrollLeft}
    >
      <section>
        <>
          <ForecastingTitleHeader />
          <ForecastingNavigationTabs />
        </>

        {!!columns?.length && !!totalAccountRows?.length && (
          <>
            {hideBudgetData ? null : (
              <div
                className={clsx(
                  styles.cashFlowContainerRightNewForecasting,
                  styles.isStickyNewForecastingRight,
                  styles.isStickyNewForecastingDesignsTop
                )}
              >
                <section data-testid="predicted-cash-grid">
                  <StickyHeaders columns={columns} headerRef={headlineHeaderRef} />
                  {hideCashGraph ? (
                    <PeriodTableControl
                      onBackClick={handlePeriodTableControl(DIRECTION.back)}
                      onForwardClick={handlePeriodTableControl(DIRECTION.forward)}
                      hideCashGraph
                    />
                  ) : totalCashPredictRow ? (
                    <DataGrid
                      totalPastDates={totalPastDates}
                      disablePastScrolling={isPayments}
                      isLoading={isFetchingReport}
                      headers={<HeaderRows totalRows={[totalCashPredictRow]} rowSize={SIZES.LARGE} />}
                      rows={
                        <Rows
                          totalRows={isPayments ? [adjustForPastDates(totalCashPredictRow)] : [totalCashPredictRow]}
                          rowHeightOverride={SIZES.LARGE}
                          columns={columns}
                          chartCustomSettings={{ yesterday: !isPayments }}
                        />
                      }
                      tableForwardRef={tables[TABLE_REF_INDICES.PREDICTED_CASH]}
                      onScroll={updateScrollLeft}
                      periodType={periodType}
                      isMainHeadlineGrid={true}
                    />
                  ) : null}
                </section>
              </div>
            )}

            {hideBudgetData && (
              <div
                className={clsx(
                  styles.cashFlowContainerRightNewForecasting,
                  styles.isStickyNewForecastingRight,
                  styles.isStickyNewForecastingDesignsTop
                )}
              >
                <section data-testid="predicted-cash-grid">
                  <StickyHeaders columns={columns} headerRef={headlineHeaderRef} />
                  {totalCashPredictRow ? (
                    <DataGrid
                      totalPastDates={totalPastDates}
                      disablePastScrolling={isPayments}
                      isLoading={isFetchingReport}
                      headers={<HeaderRows totalRows={[totalCashPredictRow]} rowSize={SIZES.LARGE} />}
                      rows={
                        <Rows
                          totalRows={isPayments ? [adjustForPastDates(totalCashPredictRow)] : [totalCashPredictRow]}
                          rowHeightOverride={SIZES.LARGE}
                          columns={columns}
                          chartCustomSettings={{ yesterday: !isPayments }}
                          hideBudgetData={true}
                        />
                      }
                      tableForwardRef={tables[TABLE_REF_INDICES.PREDICTED_CASH]}
                      onScroll={updateScrollLeft}
                      periodType={periodType}
                      isMainHeadlineGrid={true}
                      hideBudgetData={true}
                    />
                  ) : null}
                </section>
              </div>
            )}
            <div className={styles.top}>
              {!!totalAccountRows.length && (
                <div
                  className={clsx(styles.labelContainer, styles.labelContainerNewForecasting)}
                  style={hideBudgetData ? { marginTop: 0, width: rowHeaderWidth } : { width: rowHeaderWidth }}
                >
                  <ViewCollapseExpand
                    customExpandName={intl.formatMessage({
                      id: 'predict.menu.expand_accounts',
                      defaultMessage: 'Expand Accounts'
                    })}
                    customCollapseName={intl.formatMessage({
                      id: 'predict.menu.collapse_accounts',
                      defaultMessage: 'Collapse Accounts'
                    })}
                  />
                  {showDaysToPay && (
                    <div className={styles.gridLabel}>
                      <FormattedMessage id="predict.predictions.days_to_pay" defaultMessage="days to pay" />
                    </div>
                  )}
                </div>
              )}
              <div style={{ position: 'relative' }}>
                <WindowScroller scrollElement={window}>
                  {(windowScrollerChildProps: WindowScrollerChildProps) => {
                    const { registerChild } = windowScrollerChildProps as WindowScrollerChildProps & {
                      registerChild?: (element: Element | null) => void;
                    };

                    return (
                      <div ref={registerChild}>
                        <DataGrid
                          isLoading={isFetchingReport}
                          disablePastScrolling={isPayments}
                          tableForwardRef={tables[TABLE_REF_INDICES.HEADLINES]}
                          onScrollEnd={fetchForecastReport}
                          totalPastDates={totalPastDates}
                          showTableControls={false}
                          headers={
                            <HeaderRowsVirtualized
                              rows={totalAccountRows}
                              isPayments={isPayments}
                              windowScrollerChildProps={windowScrollerChildProps}
                              rowHeaderWidth={rowHeaderWidth}
                              listContainerRef={listContainerRef}
                              onListContainerMouseDown={onScrollContainerMouseDown}
                              isBudget={forecast?.kind === 'budget'}
                            />
                          }
                          onScroll={updateScrollLeft}
                          rows={
                            <RowsVirtualized
                              rows={totalAccountRows}
                              windowScrollerChildProps={windowScrollerChildProps}
                              columns={columns}
                            />
                          }
                          periodType={periodType}
                        />
                      </div>
                    );
                  }}
                </WindowScroller>
                <OverlayExplanationsContainer
                  rows={totalAccountRows}
                  rowHeights={rowHeights}
                  rowHeaderWidth={rowHeaderWidth}
                  showPredictionsExplanation={showPredictionsExplanation}
                />
              </div>
            </div>
          </>
        )}
      </section>
    </DataGridLayoutProvider>
  );
};
